import GoogleMapReact from 'google-map-react';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSupercluster from 'use-supercluster';

import { setBounds, setCenter, setZoom } from '../../reducers/mapSettingsSlice';
import { removeProperty } from '../../reducers/propertiesSlice';

import mapOptions from '../../../shared/components/map_options';

import MapControl from './components/MapControl';
import OnGoogleApiLoadedMarker from './components/OnGoogleApiLoadedMarker';
import SaleCardMap from './components/SaleCardMap';

const Marker = ({ children }) => children;

const Map = () => {
  const mapSettings = useSelector((state) => state.mapSettings);
  const { property, coordinates } = useSelector((state) => state.properties);
  const [onGoogleApiLoadedMap, setOnGoogleApiLoadedMap] = useState(null);
  const [onGoogleApiLoadedMaps, setOnGoogleApiLoadedMaps] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    handleApiLoaded(onGoogleApiLoadedMap, onGoogleApiLoadedMaps, coordinates);
  }, [coordinates]);

  const displayApproximateLocation = (lat, lng) => {
    return [
      Number(((lng * 1000 + 5) / 1000).toFixed(7)),
      Number(((lat * 1000 + 6) / 1000).toFixed(7))
    ];
  }

  const points = coordinates?.map((property) => ({
    properties: {
      cluster: false,
      zoom: mapSettings.zoom,
      propertyId: property.id,
      state: property.state,
      price: property.price,
      approximate_location: property.approximate_location
    },
    geometry: {
      type: 'Point',
      coordinates: property.approximate_location ? displayApproximateLocation(property.lat, property.lng) : [property.lng, property.lat]
    }
  }));

  // Make sure only clusters appear on page load
  let minPoints = mapSettings.mapZoom < 9 ? 1 : 6;
  const { clusters, supercluster } = useSupercluster({
    points,
    zoom: mapSettings.zoom,
    bounds: mapSettings.bounds,
    options: { radius: 80, maxZoom: 14, minPoints: minPoints }
  });

  const handleApiLoaded = (map, maps, coordinates) => {
    setOnGoogleApiLoadedMap(map);
    setOnGoogleApiLoadedMaps(maps);

    recenterMap(map, coordinates);
  };

  const zoomIn = () => {
    onGoogleApiLoadedMap?.setZoom(onGoogleApiLoadedMap.getZoom() + 1);
  };

  const zoomOut = () => {
    onGoogleApiLoadedMap?.setZoom(onGoogleApiLoadedMap.getZoom() - 1);
  };

  const recenterMap = (map, coordinates) => {
    if (typeof google === 'undefined' || typeof google.maps === 'undefined' || typeof google.maps.LatLngBounds !== 'function') {
      return;
    }

    const bounds_map = new google.maps.LatLngBounds();

    for (let i = 0; i < coordinates.length; i++) {
      bounds_map.extend(new google.maps.LatLng(coordinates[i].lat, coordinates[i].lng));
    }

    if (map && coordinates.length > 0) {
      map.fitBounds(bounds_map);

      if (coordinates.length < 2) {
        onGoogleApiLoadedMap?.setZoom(15);
      }
    }
  }

  return(
    <div id='listing-properties-map'>
      <div className='vesta-listing-properties-map'>
        <MapControl zoomIn={zoomIn} zoomOut={zoomOut} />

        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.GOOGLE_API_BROWSER_KEY }}
          defaultCenter={{ lat: 46.7111, lng: 1.7191 }}
          defaultZoom={7}
          center={mapSettings.center}
          zoom={mapSettings.zoom}
          options={mapOptions}
          onClick={() => dispatch(removeProperty())}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            handleApiLoaded(map, maps, coordinates);
          }}
          onChange={({ zoom, bounds, center }) => {
            dispatch(setZoom(zoom));
            dispatch(setCenter(center));
            dispatch(setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]));
          }}
        >
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const state = cluster.properties.state;
            const price = cluster.properties.price;
            const approximate_location = cluster.properties.approximate_location;
            const { cluster: isCluster, point_count: pointCount } = cluster.properties;
            if (isCluster) {
              return (
                <Marker key={`cluster-${cluster.id}`} lat={latitude} lng={longitude}>
                  <div
                    className='vesta-cluster-marker'
                    style={{
                      width: `${10 + (pointCount / points.length) * 40}px`,
                      height: `${10 + (pointCount / points.length) * 40}px`
                    }}
                    onClick={() => {
                      const expansionZoom = Math.max(supercluster.getClusterExpansionZoom(cluster.id), 12);
                      onGoogleApiLoadedMap.setZoom(expansionZoom);
                      onGoogleApiLoadedMap.panTo({ lat: latitude, lng: longitude });
                    }}
                  >
                    <span className='typo-bold-content'>{pointCount}</span>
                  </div>
                </Marker>
              );
            }

            return (
              <OnGoogleApiLoadedMarker
                key={cluster.properties.propertyId}
                id={cluster.properties.propertyId}
                lat={latitude}
                lng={longitude}
                approximate_location={approximate_location}
                state={state}
                price={price}
                zoom={mapSettings.zoom}
                map={onGoogleApiLoadedMap}
              />
            );
          })}
        </GoogleMapReact>

        {property && (
          <div className='vesta-map-overlay' onClick={() => dispatch(removeProperty())}>
            <SaleCardMap />
          </div>
        )}
      </div>
    </div>
  )
};

export default Map;
